import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Sprout, Bike, BookOpen, ArrowRight } from 'lucide-react';

const UCDavis: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "UC Davis";

    return (
        <div className="min-h-screen bg-white font-sans text-[#022851] selection:bg-[#ffbf00] selection:text-[#022851]">
            {/* Top Bar - Navy */}
            <div className="bg-[#022851] text-white py-2 px-6 flex justify-between items-center text-xs font-bold">
                 <div className="hidden md:flex gap-4">
                     <a href="#" className="hover:underline">Students</a>
                     <a href="#" className="hover:underline">Faculty & Staff</a>
                     <a href="#" className="hover:underline">Alumni</a>
                     <a href="#" className="hover:underline">Parents</a>
                 </div>
                <div className="flex gap-4 ml-auto">
                    <a href="#" className="hover:underline">Directory</a>
                    <a href="#" className="hover:underline">Map</a>
                    <Search className="cursor-pointer" size={16} />
                </div>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-100 shadow-sm">
                <div className="max-w-[1240px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="font-serif text-3xl font-black text-[#022851] tracking-tight leading-none text-[#022851]">{schoolName.toUpperCase()}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#022851] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#ffbf00] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#ffbf00]">About Us</a>
                        <a href="#" className="hover:text-[#ffbf00] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#ffbf00]">Admissions</a>
                        <a href="#" className="hover:text-[#ffbf00] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#ffbf00]">Academics</a>
                        <a href="#" className="hover:text-[#ffbf00] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#ffbf00]">Research</a>
                        <a href="#" className="hover:text-[#ffbf00] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#ffbf00]">Campus Life</a>
                        <a href="#" className="hover:text-[#ffbf00] transition-colors hover:underline decoration-2 underline-offset-4 decoration-[#ffbf00]">News</a>
                    </div>
                    
                     <div className="lg:hidden text-[#022851]">
                        <Menu size={28} />
                    </div>
                </div>
            </nav>

            {/* Hero - Agriculture / Bikes */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1590579491624-f98f36d4c763?q=80&w=2066&auto=format&fit=crop" 
                    alt="Arboretum" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Gold Gradient Overlay */}
                 <div className="absolute inset-0 bg-gradient-to-t from-[#022851]/80 via-transparent to-transparent"></div>

                <div className="absolute bottom-0 left-0 w-full p-12 md:p-20">
                     <div className="max-w-3xl mx-auto text-white">
                         <h1 className="text-5xl md:text-7xl font-serif font-black mb-6">
                             Outgrow the Expected.
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-medium">
                             At {schoolName}, we are conducting research that matters and solving global problems.
                         </p>
                         <Button className="bg-[#ffbf00] hover:bg-[#ffcf33] text-[#022851] font-bold uppercase tracking-widest px-10 h-14 rounded-sm shadow-md">
                             Visit Campus
                         </Button>
                     </div>
                </div>
            </header>

            {/* Icons Section */}
            <section className="bg-[#f0f5f9] py-20 px-6 md:px-12">
                <div className="max-w-[1240px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div className="p-8 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-lg transition-shadow">
                         <Sprout size={48} className="text-[#022851] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-2">#1</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">In the Nation for Agriculture</p>
                     </div>
                      <div className="p-8 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-lg transition-shadow">
                         <Bike size={48} className="text-[#022851] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-2">Platinum</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">Bicycle Friendly University</p>
                     </div>
                      <div className="p-8 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-lg transition-shadow">
                         <BookOpen size={48} className="text-[#022851] mx-auto mb-6" />
                         <h3 className="text-2xl font-bold mb-2">Top 5</h3>
                         <p className="text-gray-600 font-bold uppercase text-xs tracking-widest">Public University (Wall Street Journal)</p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="py-24 px-6 md:px-12">
                 <div className="max-w-[1240px] mx-auto">
                     <div className="flex justify-between items-center mb-12">
                         <h2 className="text-3xl font-serif font-bold text-[#022851]">News & Stories</h2>
                         <Button variant="outline" className="text-[#022851] border-[#022851] hover:bg-[#022851] hover:text-white font-bold uppercase text-xs tracking-widest">More News</Button>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                         {[
                             { title: "UC Davis Health performs rare feat in cancer surgery", img: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=2080&auto=format&fit=crop" },
                             { title: "Veterinary school treats tiger with dental issues", img: "https://images.unsplash.com/photo-1555169062-013468b477cd?q=80&w=2070&auto=format&fit=crop" },
                             { title: "New startup hub opens in downtown Davis", img: "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069&auto=format&fit=crop" }
                         ].map((item, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <div className="h-60 mb-6 overflow-hidden rounded-sm relative">
                                     <div className="absolute top-0 left-0 bg-[#ffbf00] text-[#022851] font-bold px-3 py-1 text-xs uppercase z-10 m-4 rounded-sm">Story</div>
                                     <img src={item.img} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={item.title}/>
                                 </div>
                                 <h3 className="text-xl font-bold text-[#022851] group-hover:text-[#ffbf00] transition-colors mb-4">{item.title}</h3>
                                  <div className="flex items-center gap-2 text-[#022851] font-bold uppercase text-xs tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                     Read More <ArrowRight size={14}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#022851] text-white py-16 px-6 md:px-12 border-t-8 border-[#ffbf00]">
                  <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <span className="font-serif font-black text-2xl mb-4 block">{schoolName.toUpperCase()}</span>
                          <p className="text-gray-300 mb-6">
                              1 Shields Avenue <br/>
                              Davis, CA 95616 <br/>
                              (530) 752-1011
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold text-gray-300">
                          <ul className="space-y-4 hover:[&_a]:text-[#ffbf00]">
                              <li><a href="#">UC Davis Health</a></li>
                              <li><a href="#">Veterinary Medicine</a></li>
                              <li><a href="#">Extension</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-[#ffbf00]">
                              <li><a href="#">Privacy & Accessibility</a></li>
                              <li><a href="#">Principles of Community</a></li>
                              <li><a href="#">Jobs</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1240px] mx-auto mt-16 text-center text-xs text-gray-400 border-t border-[#133e6e] pt-8">
                      © 2026 {schoolName}
                  </div>
             </footer>
        </div>
    );
};

export default UCDavis;
